import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { SearchHeader } from '../SearchHeader'
import UserContext from '../../../context/User/User'
import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { paymentStatuses, checkPermission } from '../../../utilities/helpers'
import { NoPermissionsContainer } from '../../NoPermissionsContainer'
import { PageLoadingContainer } from '../../PageLoadingContainer'
import Card from 'react-bootstrap/Card'
import { LoadingContainer } from '../../LoadingContainer'
import { EmptyContainer } from '../../EmptyContainer'
import { Pagination } from '../../Pagination'
import { EventAssignTable } from '../Results/EventAssignTable'
import { usePagination } from '../../../providers/PaginationProvider/PaginationProvider'
import { unifiedSearch } from '../../../utilities/api'
import useDebouncedValue from '../../../utilities/useDebouncedValue'
import useAbortableFetch from '../../../utilities/useAbortableFetch'
import { Link } from 'react-router-dom'
import PackageDetailsContext from '../../../context/PackageDetails/PackageDetails'
import AuthService from '../../../utilities/services/auth.service'
import { exportData } from '../../../utilities/helpers'
import Button from 'react-bootstrap/Button'
import AssignPackagesBulkModal from './AssignPackagesBulkModal'
import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { ConfirmBulkSendPackageTickets } from '../../ModalContent/ConfirmBulkSendPackageTickets'
import { BulkSendReservedPackageInvoices } from '../../ModalContent/BulkSendReservedPackageInvoices'

const statusOpts = [
  {
    label: 'All',
    value: 'all',
  },
  ...[
    ...new Map(
      Object.values(paymentStatuses).map((obj) => [JSON.stringify(obj), obj])
    ).values(),
  ],
]

export default function AssignPackages({ uuid }) {
  const didMountRef = useRef(false)
  const { canPublish, eventPackage } = useContext(PackageDetailsContext)
  // A passed sale window sets status to complete and can clear canPublish.
  // That closes public purchase. It does not block placing package holders.
  const packageAssignBlocked =
    !canPublish && eventPackage?.status !== 'complete'

  const organization = AuthService.getOrg()
  const { openModal } = useModal()

  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.SEASON_PACKAGES_MANAGEMENT
    )

  const [orders, setOrders] = useState([])

  const [isInitialState, setIsInitialState] = useState(true)

  const [isLoading, setIsLoading] = useState(true)

  const [status, setStatus] = useState(statusOpts[0].value)

  const [searchTerm, setSearchTerm] = useState('')
  const [showBulkReservedModal, setShowBulkReservedModal] = useState(false)
  const [showBulkInvoiceModal, setShowBulkInvoiceModal] = useState(false)

  // Hooks for convenience
  const debouncedQuery = useDebouncedValue(searchTerm, 300)
  const abortable = useAbortableFetch()

  const {
    currentPage,
    setCurrentPage,
    perPage,
    pageCount,
    setPageCount,
    resultsPerPageOpts,
    handleChange,
  } = usePagination()

  const runSearch = useCallback(
    async (isFirstTime = false) => {
      setIsLoading(true)
      try {
        const res = await abortable((signal) =>
          unifiedSearch(
            {
              organizationUUID: organization?.[0]?.uuid,
              uuid: uuid,
              by: 'package',
              sourceType: 'ticket_assignment',
              searchTerm: debouncedQuery,
              paymentStatus: status,
              page: currentPage + 1,
              perPage: perPage,
            },
            signal
          )
        )

        const {
          data,
          meta: {
            pagination: { pageCount: pc },
          },
        } = res.data

        setOrders(data)
        setPageCount(pc)
        setIsLoading(false)
      } catch (err) {
        if (err?.name !== 'CanceledError') {
          console.error(err)
          setIsLoading(false)
        }
      } finally {
        setIsInitialState(isFirstTime)
        setIsLoading(false)
      }
    },
    [
      abortable,
      uuid,
      currentPage,
      perPage,
      debouncedQuery,
      status,
      setPageCount,
    ]
  )

  const handleExportAllPackageHolders = async () => {
    try {
      const organizationUUID = organization?.[0]?.uuid
      if (!organizationUUID || !uuid) return

      const perPageForExport = 500
      let page = 1
      let pageCount = 1
      const allOrders = []

      do {
        const res = await unifiedSearch({
          organizationUUID,
          uuid,
          by: 'package',
          sourceType: 'ticket_assignment',
          searchTerm: '',
          paymentStatus: 'all',
          page,
          perPage: perPageForExport,
        })

        const responseData = res?.data?.data || []
        const pagination = res?.data?.meta?.pagination || {}
        pageCount = Number(pagination.pageCount || 1)

        allOrders.push(...responseData)
        page += 1
      } while (page <= pageCount)

      exportData(
        allOrders,
        'package_holders',
        undefined,
        allOrders?.[0]?.package?.name,
        undefined,
        organization?.[0]?.name
      )
    } catch (err) {
      console.error('Failed to export all package holders', err)
    }
  }

  const handleStatusChange = (value) => {
    setStatus(value)
  }

  const openBulkSendTickets = () => {
    openModal(
      ConfirmBulkSendPackageTickets,
      { packageUUID: uuid, onSuccess: runSearch },
      { className: 'modal--lg', contentClassName: '' }
    )
  }

  useEffect(() => {
    if (!didMountRef.current) return // ⛔ skip on first render - to prevent duplicate runSearch calls
    runSearch() // ✅ run only for real changes
  }, [currentPage, perPage])

  useEffect(() => {
    if (currentPage === 0 && didMountRef.current) {
      runSearch()
      return
    }

    setCurrentPage(0)
  }, [debouncedQuery, status])

  useEffect(() => {
    if (!orgPermissionsReady || !hasPermission) return
    runSearch(true) // True as in it is initial query
    didMountRef.current = true
    // run when org permissions are ready and season packages management is allowed
  }, [orgPermissionsReady, hasPermission])

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  return (
    <>
      <div className="position-relative">
        <section
          className="max-width-wrapper"
          data-testid="assign-packages-section"
        >
          <Header
            organization={organization[0]}
            isInitialState={isInitialState}
            orders={orders}
            status={status}
            handleStatusChange={handleStatusChange}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            uuid={uuid}
            onExport={handleExportAllPackageHolders}
            onOpenBulkReserved={() => setShowBulkReservedModal(true)}
            onOpenBulkSendTickets={openBulkSendTickets}
            onOpenBulkInvoices={() => setShowBulkInvoiceModal(true)}
            assignDisabled={packageAssignBlocked}
          />
          <Results
            orders={orders}
            setOrders={setOrders}
            isInitialState={isInitialState}
            isLoading={isLoading}
            runSearch={runSearch}
            pagination={{
              currentPage,
              setCurrentPage,
              perPage,
              pageCount,
              setPageCount,
              resultsPerPageOpts,
              handleChange,
            }}
          />
        </section>
      </div>
      <AssignPackagesBulkModal
        packageUUID={uuid}
        show={showBulkReservedModal}
        onHide={() => setShowBulkReservedModal(false)}
        disabled={packageAssignBlocked}
        onCompleted={runSearch}
      />
      <BulkSendReservedPackageInvoices
        packageUUID={uuid}
        show={showBulkInvoiceModal}
        onHide={() => setShowBulkInvoiceModal(false)}
        onSuccess={runSearch}
      />
    </>
  )
}

function Results({
  orders,
  setOrders,
  isInitialState,
  isLoading,
  pagination,
  runSearch,
}) {
  if (isLoading) {
    return <LoadingContainer />
  }

  // first time render and no orders - won't trigger when searching
  if (isInitialState && !orders?.length) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No package orders</p>
          <p className="subtitle">
            Use Import from Excel or Pick seats on map above to add your first
            package order.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (orders?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No results found...</p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <>
      <EventAssignTable
        orders={orders}
        setOrders={setOrders}
        runSearch={runSearch}
      />
      <Pagination
        pageCount={pagination.pageCount}
        perPage={pagination.perPage}
        currentPage={pagination.currentPage}
        resultsPerPageOpts={pagination.resultsPerPageOpts}
        handleClick={(value) => pagination.setCurrentPage(value.selected)}
        handleChange={pagination.handleChange}
      />
    </>
  )
}

function Header({
  isInitialState,
  orders,
  organization,
  status,
  handleStatusChange,
  setSearchTerm,
  uuid,
  onExport,
  onOpenBulkReserved,
  onOpenBulkSendTickets,
  onOpenBulkInvoices,
  assignDisabled,
}) {
  return (
    <SearchHeader
      actionsRightComponent={
        <Button
          variant="outline-light"
          size="lg"
          onClick={onOpenBulkSendTickets}
          aria-label="Send tickets to every complete package order that has not been sent"
        >
          Send all tickets
        </Button>
      }
      selectOptions={{
        show: true,
        options: statusOpts,
        value: status,
        handleChange: handleStatusChange,
        label: 'Filter By Status',
        labelId: 'filter-by-status',
      }}
      showActions={!isInitialState || orders?.length > 0}
      rightSideComponent={
        <div className="d-flex flex-column align-items-end gap-3">
          <Button
            variant="outline-light"
            size="lg"
            onClick={onOpenBulkInvoices}
            aria-label="Send invoices to reserved season ticket holders"
          >
            Send invoices
          </Button>
          <div className="d-flex flex-column align-items-end gap-2">
            <span className="text-muted small text-end lh-sm mb-0">
              Two ways to add holders
            </span>
            <div className="d-flex flex-wrap gap-2 justify-content-end align-items-center">
              <Button
                variant="outline-light"
                size="lg"
                className="btn-plus"
                disabled={assignDisabled}
                onClick={onOpenBulkReserved}
                aria-label="Import reserved package holders from an Excel spreadsheet"
              >
                Import from Excel
              </Button>
              <Link
                className={`btn btn-outline-light btn-lg btn-plus ${
                  assignDisabled ? 'btn-link-disabled' : ''
                }`}
                to={`/package/${uuid}/seatmap/assign`}
                aria-label="Open the seat map to choose seats for package holders"
              >
                Pick seats on map
              </Link>
            </div>
          </div>
        </div>
      }
      title="Assign package"
      desc="Import many holders from a spreadsheet, or open the seat map to choose seats for each fan."
      searchPlaceholder="Search by email"
      setSearchTerm={setSearchTerm}
      data={orders}
      exportedData="invoices"
      packageName={orders[0]?.package?.name}
      organizationName={organization?.name}
      downloadReport={onExport}
    />
  )
}
